import { useCallback, useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { useLoading, usePrevious, useServerError } from 'shared/hooks'

import { GET_ARTIST_PRODUCTS } from 'modules/ProjectCreate/graphql/queries'
import { IProductFormArray, IProjectForm } from 'modules/ProjectCreate/types'
import { IProduct } from 'shared/interfaces'

const defaultValues = {
  searchedName: '',
  name: '',
  configuration: 'single',
  gps_status: '',
  publish_date: '',
  radio_updates: true,
  dsp_updates: true,
  isSaved: false
}

const useProductTable = () => {
  const [editingRowIdx, setEditingRow] = useState<null | number>(null)

  const [getArtistProducts, { data: artistProductsData, loading: artistProductsLoading, error: artistProductsError }] =
    useLazyQuery<{ getArtistProducts: IProduct[] }, { name: string; artist_id: string; }>(GET_ARTIST_PRODUCTS)

  const { control, getValues, trigger, reset } = useFormContext<IProjectForm>()
  const { fields, append, remove, update } = useFieldArray<IProjectForm, 'products', 'products'>({
    control,
    name: 'products'
  })

  const artist_id = getValues('artistId')
  const prevArtistId = usePrevious(artist_id)
  const addProduct = useCallback(() => {
    append(defaultValues)

    setEditingRow(fields.length)
  }, [append, fields.length])

  const handleRowClose = useCallback(
    (idx: number, defaultProduct: IProductFormArray | null) => {
      if (defaultProduct) {
        update(idx, defaultProduct)
      } else {
        remove(idx)
      }
      setEditingRow(null)
    },
    [remove, update]
  )

  const handleAddProduct = useCallback(
    async (idx: number) => {
      const isValid = await trigger()
      if (!isValid) return

      const product = getValues(`products.${idx}`)
      update(idx, { ...product, isSaved: true })
      setEditingRow(null)
    },
    [getValues, trigger, update]
  )

  const handleRowEdit = useCallback(
    (idx: number) => {
      if (editingRowIdx && editingRowIdx < fields.length) {
        const { isSaved } = getValues(`products.${editingRowIdx}`)
        if (!isSaved) {
          remove(editingRowIdx)
        }
      }
      setEditingRow(idx)
    },
    [editingRowIdx, fields, getValues, remove]
  )

  useEffect(() => {
    if (artist_id || prevArtistId !== artist_id)
      getArtistProducts({
        variables: {
          name: '',
          artist_id
        }
      })
  }, [artist_id, prevArtistId, getArtistProducts])

  useEffect(() => {
    if (artistProductsData) {
      const value: IProduct[] = artistProductsData?.getArtistProducts || []
      reset({
        ...getValues(),
        artistProducts: value
      })
    }
  }, [artistProductsData, reset, getValues])

  useLoading({ isLoading: artistProductsLoading })
  useServerError([artistProductsError])

  return {
    fields,
    editingRowIdx,
    handleRowEdit,
    addProduct,
    handleRowClose,
    handleAddProduct
  }
}

export default useProductTable
